// Interface language on the fronts: a small store read by React (`useT`,
// `useLocale`) and by plain modules (`t()`: API errors, toasts raised outside
// React). The admin never calls `startLocale`, so it stays in French.
//
// Order: the account's language once the session is known (`LocaleSync` in
// the app), else `?lang=` (links from the marketing site), else the
// `hune-locale` cookie (a previous choice), else the browser's languages.
// The cookie is what the API reads for requests made without an account
// (signup, Google sign-in, forgotten password).
import { useSyncExternalStore } from "react";
import { DEFAULT_LOCALE, INTL_LOCALE, LOCALE_COOKIE, isLocale, matchLocale, messages, type Locale } from "@shared/i18n";

let current: Locale = DEFAULT_LOCALE;
const listeners = new Set<() => void>();

function readCookie() {
  const match = document.cookie.match(new RegExp(`(?:^|; )${LOCALE_COOKIE}=([^;]*)`));
  return match ? decodeURIComponent(match[1]) : null;
}

function apply(locale: Locale) {
  document.documentElement.lang = locale;
  // A year, on the app's own host: the API reads it on the same origin.
  document.cookie = `${LOCALE_COOKIE}=${locale}; Path=/; Max-Age=31536000; SameSite=Lax`;
}

/** The language detected for a visitor before any account is known. */
export function detectLocale(): Locale {
  const fromUrl = new URLSearchParams(window.location.search).get("lang");
  if (isLocale(fromUrl)) return fromUrl;
  const fromCookie = readCookie();
  if (isLocale(fromCookie)) return fromCookie;
  return matchLocale(navigator.languages?.length ? navigator.languages : [navigator.language]) ?? DEFAULT_LOCALE;
}

/** Called once at boot by the app: detects and remembers the visitor's language. */
export function startLocale() {
  current = detectLocale();
  apply(current);
}

export function getLocale() {
  return current;
}

export function setLocale(locale: Locale) {
  if (locale === current) return;
  current = locale;
  apply(locale);
  for (const listener of listeners) listener();
}

function subscribe(listener: () => void) {
  listeners.add(listener);
  return () => listeners.delete(listener);
}

export function useLocale(): Locale {
  return useSyncExternalStore(subscribe, getLocale, () => DEFAULT_LOCALE);
}

/** The copy of the current language, for a component. */
export function useT() {
  return messages(useLocale());
}

/** The copy of the current language, outside React. */
export function t() {
  return messages(current);
}

/** `Intl` locale of the current language (dates, numbers). */
export function useIntlLocale() {
  return INTL_LOCALE[useLocale()];
}
